import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ListMaintenancesScreen.module.scss';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, SEGMENT_FEATURES } from 'Config';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import VehicleMaintenancesService from 'api/vehicleMaintenances/VehicleMaintenancesService';
import Loading from 'common/services/Loading';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import Popover from 'common/components/popover/Popover';
import { VehicleMaintenanceCounterListDto, VehicleMaintenanceDto } from 'api/vehicleMaintenances/models/VehicleMaintenanceDto';
import { VehicleMaintenancesSearchCriteria } from 'api/vehicleMaintenances/models/VehicleMaintenancesSearchCriteria';
import { VehicleMaintenanceStates } from 'api/vehicleMaintenances/enums/VehicleMaintenanceStates';
import MaintenanceFiltersScreen, { Filters } from 'screens/maintenances/components/maintenanceFilters/MaintenancesFiltersScreen';
import { useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import MaintenanceListTable from './MaintenanceListTable/MaintenanceListTable';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import VehiclesService from 'api/vehicles/VehiclesService';
import InfoCard from 'common/components/infoCard/InfoCard';
import CoinsIcon from 'assets/svg/coins.svg';
import CarIcon from 'assets/svg/car.svg';
import MaintenancesIcon from 'assets/svg/maintenance.svg';
import ReactTooltip from 'react-tooltip';
import colors from 'styles/export/colors.module.scss';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

// Array de cores para os info cards
const INFO_CARD_COLORS = [
    colors.counterTeal1,  // #02C7C7
    colors.counterTeal2,  // #0CB6D1
    colors.counterBlue1,  // #0FA7D4
    colors.counterBlue2,  // #038CB6
];


export type Props = {
    criteria?: VehicleMaintenancesSearchCriteria;
    editable: boolean,
    onClickItem: (item: VehicleMaintenanceDto, isDetails: boolean, event: any) => void;
    createNewMaintenance: () => void;
    onDeleteFromList?: () => void;
    tourReRender?: () => void;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const ListMaintenancesScreen = ({ criteria: _criteria, editable, onClickItem, createNewMaintenance, onDeleteFromList, tourReRender, exportRef, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [maintenances, setMaintenances] = useState<VehicleMaintenanceDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<VehicleMaintenanceCounterListDto>();
    const { vehicleId, month } = useParams<{ vehicleId: string, month: string }>();

    const internalExportRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);
    const effectiveExportRef = vehicleId ? internalExportRef : exportRef;

    const FILTERS_CACHE_KEY = 'vehicle-maintenance-filters-' + (vehicleId ? 1 : '0');

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        isScrollRestoring,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const hasVehicleMaintenancesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_MAINTENANCES_WRITE']);

    const [criteria, setCriteria] = useState<VehicleMaintenancesSearchCriteria>(_criteria || {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'vm.maintenance_date',
        orderBy: 'desc',
        vehicleId: vehicleId,
        executedStartDate: month ? new Date(month) : undefined,
        executedEndDate: month ? new Date(new Date(new Date(new Date(month).setMonth(new Date(month).getMonth() + 1)).setUTCHours(0)).setUTCSeconds(-1)) : undefined,
        currentState: month ? VehicleMaintenanceStates.PERFORM : undefined,
        translations: t('common.maintenance_contract_type', { returnObjects: true })
    });

    const [filters, setFilters] = useState<Filters>(month ? {
        executedStartDate: criteria.executedStartDate,
        executedEndDate: criteria.executedEndDate,
        currentState: VehicleMaintenanceStates.PERFORM,
        currentStateName: t('maintenances.states.PERFORM') as string,
        vehicleId: vehicleId,
    } : {});
    const [filtersTotal, setFiltersTotal] = useState(month ? 2 : 0);
    const [vehicleSegmentFeatures, setVehicleSegmentFeatures] = useState<string[]>([])


    const segmentFeaturesLoaded = useRef<boolean>(false);
    const isFirstLoad = useRef<boolean>(true);
    const lastFilteredVehicleId = useRef<string | undefined>(vehicleId);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);
    const previousCriteria = useRef<VehicleMaintenancesSearchCriteria>(criteria);
    const countersLoaded = useRef<boolean>(false);

    const getData = async (isLoadMore = false, shouldUpdateCounters = false) => {
        if (!segmentFeaturesLoaded.current) {
            getSegmentFeatures();
            return;
        }

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            criteria.vehicleId = vehicleId ?? criteria.vehicleId;

            saveOrdernationFilters();

            const res = await VehicleMaintenancesService.getList(criteria);
            setTotalItems(res.totalItems);

            if (isLoadMore) {
                // Accumulate items on subsequent pages
                setMaintenances(prev => [...prev, ...res.items]);
                // Update loadedPageRange.max after successful load
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                // Reset on first page
                setMaintenances(res.items);
            }

            if (shouldUpdateCounters) {
                await getCounters();
                countersLoaded.current = true;
            }

            ReactTooltip.rebuild();

            Loading.hide();
            setIsLoadingMore(false);

            // When restoring scroll, load adjacent pages (previous and next) to have enough content to scroll
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(res.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                // Load previous page if exists
                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await VehicleMaintenancesService.getList(prevCriteria);
                        setMaintenances(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't load previous page`, e);
                    }
                }

                // Load next page if exists
                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await VehicleMaintenancesService.getList(nextCriteria);
                        setMaintenances(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't load next page`, e);
                    }
                }

                // Trigger scroll to the target item
                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't get maintenance list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        }
        finally {
            tourReRender && tourReRender();
        }
    };

    const getCounters = async () => {
        try {
            const countersDB = await VehicleMaintenancesService.getCountersList(criteria);
            setCounters(countersDB);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get vehicles maintenances counters`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const renderCounterBoxes = () => {
        return (
            <div className={styles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('maintenances.list.number_vehicles') as string}
                        value={counters.numberVehicles ?? 0}
                        icon={CarIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('maintenances.list.number_maintenances') as string}
                        value={counters.numberMaintenances ?? 0}
                        icon={MaintenancesIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('maintenances.list.total_cost') as string}
                        value={counters.costTotalAccumulated ?? 0}
                        icon={CoinsIcon}
                        backgroundColor={INFO_CARD_COLORS[2]}
                        isMoney
                    />
                )}
            </div>
        );
    }

    const renderCounterBoxForTab = () => {
        return (
            <div className={styles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('maintenances.list.number_maintenances') as string}
                        value={counters.numberMaintenances ?? 0}
                        icon={MaintenancesIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                        className={styles.col2}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('maintenances.list.total_cost') as string}
                        value={counters.costTotalAccumulated ?? 0}
                        icon={CoinsIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                        isMoney
                        className={styles.col2}
                    />
                )}
            </div>
        );
    }

    const getSegmentFeatures = async (filterVehicleId?: string) => {
        try {
            Loading.show();
            let features: string[] = [];

            const targetVehicleId = filterVehicleId ?? vehicleId ?? criteria.vehicleId;

            if (targetVehicleId) {
                features = await VehiclesService.getVehicleSegmentFeatures(targetVehicleId);
            }

            segmentFeaturesLoaded.current = true;

            setVehicleSegmentFeatures(features ?? [])

            setCriteria(prev => ({
                ...prev,
                orderColumn: criteria?.orderColumn ?? (showField(SEGMENT_FEATURES.KM, features) ? 'vm.kilometers' : 'vm.maintenance_date')
            }));

        } catch (error) {
            segmentFeaturesLoaded.current = true;
            getData(false, true);
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't get vehicle segment features`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        // Reset items when sorting changes
        setMaintenances([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    }

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingMore || maintenances.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, maintenances.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            // Store scroll height before prepending
            prepareLoadPrevious();

            const prevCriteria: VehicleMaintenancesSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await VehicleMaintenancesService.getList(prevCriteria);

            // Prepend items to the list
            setMaintenances(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCES, `Couldn't get previous maintenance page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    const fetchAllForExport = useCallback(async () => {
        const result = await VehicleMaintenancesService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    // Adjust scroll position after prepending items (bidirectional scroll)
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [maintenances, isLoadingPrevious, adjustScrollAfterPrepend]);

    // Note: scrollToItemId clearing and scroll restoration are handled by the useBidirectionalScroll hook

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        // Reset items when filters change (for infinite scroll)
        if (!isOnLoad) {
            setMaintenances([]);
            resetScrollState();
            setTotalItems(0);
        }

        // Reload segment features if vehicle filter changed
        if (lastFilteredVehicleId.current !== f.vehicleId) {
            lastFilteredVehicleId.current = f.vehicleId;
            getSegmentFeatures(f.vehicleId);
        }

        setCriteria(prev => ({
            ...prev,
            startDate: f.startDate,
            endDate: f.endDate,
            vehicleId: f.vehicleId,
            vehicleName: f.vehicleName,
            currentState: f.currentState,
            currentStateName: f.currentStateName,
            supplierId: f.supplierId,
            supplierName: f.supplierName,
            responsibleId: f.responsibleId,
            responsibleName: f.responsibleName,
            typeId: f.typeId,
            typeName: f.typeName,
            executedStartDate: f.executedStartDate,
            executedEndDate: f.executedEndDate,
            vehiclesInactive: f.vehiclesInactive,
            orderBy: f.orderBy,
            orderColumn: f.orderColumn,
            page: 1, // Always reset to page 1
            componentIds: f.componentIds,
            reminderPredicted: f.reminderPredicted,
            reminderScheduling: f.reminderScheduling,
            localIds: f.localIds,
            localOperator: f.localOperator,
            segmentId: f.segmentId,
            upcomingKmPercent: f.upcomingKmPercent,
            upcomingHoursPercent: f.upcomingHoursPercent,
            overdueByMeter: f.overdueByMeter,
        }))
        setReRenderSearch(r => r + 1);
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.executedStartDate || f.executedEndDate) {
            total++;
        }
        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.currentState) {
            total++;
        }
        if (f.typeId) {
            total++;
        }
        if (f.supplierId) {
            total++;
        }
        // if (f.vehicleId) {
        //     total++;
        // }
        if (f.responsibleId) {
            total++;
        }
        if (f.componentIds && f.componentIds.length > 0) {
            total++;
        }

        setFiltersTotal(total);
    }

    const showField = (feature: string, features: string[] = vehicleSegmentFeatures) => {
        return (features.findIndex(x => x == feature) ?? -1) >= 0;
    }

    useEffect(() => {
        if (_criteria && !isFirstLoad.current) {
            onChangeFilters(_criteria);
        }
    }, [_criteria]);

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            // Detect if it's a load more (page > max loaded page and items already exist)
            const isLoadMore = criteria.page > loadedPageRange.max && maintenances.length > 0;

            // Check if only order/page changed (not filters that affect counters)
            const prev = previousCriteria.current;
            const onlyOrderOrPageChanged =
                prev.startDate === criteria.startDate &&
                prev.endDate === criteria.endDate &&
                prev.vehicleId === criteria.vehicleId &&
                prev.currentState === criteria.currentState &&
                prev.supplierId === criteria.supplierId &&
                prev.responsibleId === criteria.responsibleId &&
                prev.typeId === criteria.typeId &&
                prev.executedStartDate === criteria.executedStartDate &&
                prev.executedEndDate === criteria.executedEndDate &&
                prev.vehiclesInactive === criteria.vehiclesInactive &&
                prev.allIn === criteria.allIn &&
                JSON.stringify(prev.componentIds) === JSON.stringify(criteria.componentIds) &&
                prev.reminderPredicted === criteria.reminderPredicted &&
                prev.reminderScheduling === criteria.reminderScheduling &&
                JSON.stringify(prev.localIds) === JSON.stringify(criteria.localIds) &&
                prev.localOperator === criteria.localOperator &&
                prev.segmentId === criteria.segmentId;

            // Always load counters on first data fetch, or when filters changed
            const shouldLoadCounters = !countersLoaded.current || !onlyOrderOrPageChanged;
            getData(isLoadMore, shouldLoadCounters);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null && !month) {
            const f = JSON.parse(cachedFilters);

            // If returning to list with saved scroll position
            if (f.scrollToItemId) {
                // Use the hook to initiate restoration
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: f.scrollToItemId,
                });

                // Clear scroll position from cache
                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);
                updateTotalFilters(cleanedFilters);

                // Set criteria to start at the saved page
                setCriteria(prev => ({
                    ...prev,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    vehicleId: f.vehicleId,
                    vehicleName: f.vehicleName,
                    currentState: f.currentState,
                    currentStateName: f.currentStateName,
                    supplierId: f.supplierId,
                    supplierName: f.supplierName,
                    responsibleId: f.responsibleId,
                    responsibleName: f.responsibleName,
                    typeId: f.typeId,
                    typeName: f.typeName,
                    executedStartDate: f.executedStartDate,
                    executedEndDate: f.executedEndDate,
                    vehiclesInactive: f.vehiclesInactive,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    componentIds: f.componentIds,
                    localIds: f.localIds,
                    segmentId: f.segmentId,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));
                isFirstLoad.current = false;
                return;
            }

            // Normal load without scroll restoration
            f.scrollToItemId = undefined;
            f.page = 1;
            resetScrollState();
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));

            if (!_criteria) {
                onChangeFilters(f, true)
            } else {
                // Merge cached filter values with parent criteria
                // Cache takes priority for filter fields since parent may not have loaded yet
                const merged = {
                    ...f,
                    ..._criteria,
                    vehicleId: f.vehicleId ?? _criteria.vehicleId,
                    startDate: f.startDate ?? _criteria.startDate,
                    endDate: f.endDate ?? _criteria.endDate,
                    currentState: f.currentState ?? _criteria.currentState,
                    supplierId: f.supplierId ?? _criteria.supplierId,
                    responsibleId: f.responsibleId ?? _criteria.responsibleId,
                    typeId: f.typeId ?? _criteria.typeId,
                    executedStartDate: f.executedStartDate ?? _criteria.executedStartDate,
                    executedEndDate: f.executedEndDate ?? _criteria.executedEndDate,
                    vehiclesInactive: f.vehiclesInactive,
                    componentIds: f.componentIds ?? _criteria.componentIds,
                    reminderPredicted: f.reminderPredicted ?? _criteria.reminderPredicted,
                    reminderScheduling: f.reminderScheduling ?? _criteria.reminderScheduling,
                    localIds: f.localIds ?? _criteria.localIds,
                    localOperator: f.localOperator ?? _criteria.localOperator,
                    segmentId: f.segmentId ?? _criteria.segmentId,
                    orderBy: f.orderBy || _criteria.orderBy,
                    orderColumn: f.orderColumn || _criteria.orderColumn,
                    page: 1,
                };
                onChangeFilters(merged, true);
            }
        }
        else { getData(false, true) }

        isFirstLoad.current = false;
    }

    const onRowClick = (item: VehicleMaintenanceDto, isDetails: boolean, event: any) => {
        // Calculate the actual page of the clicked item based on its position in the list
        const itemIndex = maintenances.findIndex(m => m.id === item.id);
        const actualPage = calculatePageFromIndex(itemIndex);

        // Save scroll position with actual page and item id
        const newFilters = { ...filters };
        newFilters.page = actualPage;
        newFilters.scrollToItemId = item.id;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        onClickItem(item, isDetails, event);
    }

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        // Don't save page - always start from page 1
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    }

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }))
        // Keep page and scrollToItemId when saving scroll position, otherwise don't persist page
        if (f.scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page, scrollToItemId, ...filtersWithoutScrollData } = f;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filtersWithoutScrollData));
        }
    }

    return (
        <div className={styles.listWrapper}>
            {vehicleId &&
                <div className={styles.tabHeader}>
                    <div>
                        <InputSearch inputContainerClassName={styles.inputContainer} onChangeValue={debounced} placeholder={t('maintenances.list.search')} />
                    </div>
                    <div className={styles.rowFlex}>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['left']}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen =>
                                <MaintenanceFiltersScreen
                                    filters={filters}
                                    onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                                    onChange={_filters => updateTotalFilters(_filters)}
                                />
                            }
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}

                        </Popover>
                        <ExportListingButton exportRef={internalExportRef} />
                        {hasVehicleMaintenancesWritePolicy && editable && <ScreenHeaderButton icon={AddIcon} onClick={createNewMaintenance} />}
                    </div>
                </div>
            }
            {vehicleId && counters &&
                <div className={styles.countersSection}>{renderCounterBoxForTab()}</div>
            }
            { counters && (!vehicleId) && <>
                <div className={styles.countersSection}> {renderCounterBoxes()} </div>
            </>
            }

            {maintenances.length > 0 || totalItems === 0 ? (
                <div className={styles.listTblWrapper}>
                    <MaintenanceListTable
                        reRenderSearch={reRenderSearch}
                        items={maintenances}
                        totalItems={totalItems}
                        isLoadingMore={isLoadingMore}
                        hasMore={getHasMore(maintenances.length, totalItems)}
                        vehicleId={vehicleId}
                        vehicleSegmentFeatures={vehicleSegmentFeatures}
                        editable={editable}
                        onLoadMore={getOnLoadMore(loadMore)}
                        onTableFilter={onTableFilter}
                        onClickItem={onRowClick}
                        onDeleteFromList={() => {
                            // Reset items and reload after delete
                            setMaintenances([]);
                            resetScrollState();
                            countersLoaded.current = false; // Force counters reload
                            setCriteria(prev => ({ ...prev, page: 1 }));
                            onDeleteFromList && onDeleteFromList();
                        }}
                        criteria={criteria}
                        scrollToRowId={scrollToItemId}
                        hasMorePrevious={getHasMorePrevious()}
                        isLoadingPrevious={isLoadingPrevious}
                        onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                        onFetchAllForExport={fetchAllForExport}
                        exportRef={effectiveExportRef}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default ListMaintenancesScreen;
